import React, { useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import { AnimatePresence, motion } from "framer-motion";
import { LUXURY } from "data/guestLuxuryTheme";
import { guestCopy } from "data/guestCopy";
import { HUB_CATEGORIES } from "data/guestPortalDefaults";
import HubFilterBar from "./HubFilterBar";

const groupIcon = (group) => {
  if (group === "Food") return <RestaurantIcon sx={{ fontSize: 16 }} />;
  if (group === "Events") return <InfoOutlinedIcon sx={{ fontSize: 16 }} />;
  if (group === "Info") return <InfoOutlinedIcon sx={{ fontSize: 16 }} />;
  return <RoomServiceIcon sx={{ fontSize: 16 }} />;
};

const HubSearch = ({
  open,
  onClose,
  value,
  onChange,
  filter,
  onFilterChange,
  results,
  onSelect,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => inputRef.current?.focus(), 180);
    return () => window.clearTimeout(id);
  }, [open]);

  return (
    <AnimatePresence initial={false}>
      {open ? (
        <Box
          component={motion.div}
          key="hub-search-panel"
          initial={{ opacity: 0, height: 0, y: -8 }}
          animate={{ opacity: 1, height: "auto", y: 0 }}
          exit={{ opacity: 0, height: 0, y: -8 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          sx={{ overflow: "hidden", mb: 1.6 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: LUXURY.cardSoft,
                borderRadius: "999px",
                border: `1px solid ${LUXURY.cardBorder}`,
                px: 1.6,
                height: 50,
              }}
            >
              <SearchIcon sx={{ color: LUXURY.gold, fontSize: 20 }} />
              <Box
                component="input"
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={guestCopy.searchPlaceholder}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  bgcolor: "transparent",
                  fontSize: "0.88rem",
                  color: LUXURY.ink,
                  fontFamily: LUXURY.bodyFont,
                  "::placeholder": { color: LUXURY.stone },
                }}
              />
              {value ? (
                <IconButton
                  size="small"
                  onClick={() => onChange("")}
                  sx={{ color: LUXURY.hint }}
                  aria-label="Clear search"
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              ) : null}
            </Box>
            <IconButton
              aria-label="Close search"
              onClick={onClose}
              sx={{
                width: 50,
                height: 50,
                borderRadius: "16px",
                bgcolor: LUXURY.cardSoft,
                border: `1px solid ${LUXURY.cardBorder}`,
                color: LUXURY.gold,
                "&:hover": { bgcolor: LUXURY.forestMid },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box sx={{ mt: 1.15 }}>
            <HubFilterBar
              items={HUB_CATEGORIES}
              value={filter}
              onChange={onFilterChange}
            />
          </Box>

          {value.trim() ? (
            <Paper
              elevation={0}
              sx={{
                mt: 1.15,
                p: 0.75,
                borderRadius: "16px",
                border: `1px solid ${LUXURY.cardBorder}`,
                maxHeight: 280,
                overflow: "auto",
                bgcolor: LUXURY.card,
              }}
            >
              {results.length === 0 ? (
                <Typography sx={{ px: 1.2, py: 1.6, fontSize: "0.85rem", color: LUXURY.muted }}>
                  Nothing matched. Try a dish, towels, or Wi-Fi.
                </Typography>
              ) : (
                <Stack spacing={0.35}>
                  {results.map((result) => (
                    <Box
                      key={`${result.group}-${result.id}`}
                      onClick={() => onSelect(result)}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        px: 1.1,
                        py: 1.05,
                        minHeight: 48,
                        borderRadius: "12px",
                        cursor: "pointer",
                        "&:hover": { bgcolor: alpha(LUXURY.gold, 0.12) },
                      }}
                    >
                      <Box sx={{ color: LUXURY.gold, flexShrink: 0, mt: 0.2 }}>
                        {groupIcon(result.group)}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.86rem",
                            color: LUXURY.ink,
                            lineHeight: 1.3,
                            wordBreak: "break-word",
                          }}
                        >
                          {result.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            color: LUXURY.muted,
                            lineHeight: 1.4,
                            wordBreak: "break-word",
                          }}
                        >
                          {result.group}
                          {result.subtitle ? ` · ${result.subtitle}` : ""}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          ) : null}
        </Box>
      ) : null}
    </AnimatePresence>
  );
};

export default HubSearch;
